import type { IkeAction } from '../action';
import type { CollectionSelector } from '../common/collection.selector';
import type { IkeUnitDistance } from '../const/units';

/** Fast exports — synchronous response, data returned directly (no S3 artifact) */
export type FastExportType = 'KML' | 'JSON';

/** Long-running exports — background task, cached S3 artifact, pollable status */
export type LongRunningExportType =
	| 'EXCEL'
	| 'OCALC'
	| 'SPIDACALC'
	| 'IMAGE_ZIP'
	| 'POLEFOREMAN'
	| 'IKE_POLEFOREMAN'
	| 'IMPORTABLE_EXCEL'
	| 'DDS'
	| 'SHAPEFILE'
	| 'KML_VARIANTS'
	| 'JSON_VARIANTS'
	| 'EXCEL_VARIANTS';

/** Union of all export types */
export type ExportType = FastExportType | LongRunningExportType;

export const FAST_EXPORT_TYPES: ReadonlySet<string> = new Set<FastExportType>(['KML', 'JSON']);
export const LONG_RUNNING_EXPORT_TYPES: ReadonlySet<string> = new Set<LongRunningExportType>([
	'EXCEL',
	'OCALC',
	'SPIDACALC',
	'IMAGE_ZIP',
	'POLEFOREMAN',
	'IKE_POLEFOREMAN',
	'IMPORTABLE_EXCEL',
	'DDS',
	'SHAPEFILE',
	'KML_VARIANTS',
	'JSON_VARIANTS',
	'EXCEL_VARIANTS',
]);

/**
 * A selected "Include variants" checkbox in the export-variants modal, sent to the server via
 * `ExportTaskPayload.variantGroups` to pick which per-group files a variant-aware export
 * produces. The checkbox list itself is built dynamically from whatever `variantType`s are
 * actually present in the current selection (plus the always-present "Existing" checkbox) — there
 * is no fixed/closed set, since PoleOS's `variantType` is an open union (known values today:
 * asis/design/polepilot/mre/proposed, per `poleos-variant-relay`'s `IPoleosEtlExportMetadata`).
 *
 * `EXISTING_VARIANT_GROUP` (the empty string) is synthetic: it selects the parent (non-variant)
 * collections themselves, matching the `variantType: ''` convention already used for the parent's
 * row elsewhere (see `buildVariantRows` in client-ui). Any other value is a literal `variantType`
 * to filter a parent's `variants[]` by.
 */
export type VariantExportGroup = string;

export const EXISTING_VARIANT_GROUP = '';

/**
 * A checked "Include variants" checkbox, sent to the server as-is so the resulting file is named
 * exactly what the user saw in the modal, and so filtering matches exactly what was checked.
 *
 * `variantType` alone is not a unique checkbox identity: two variants can share the same
 * `variantType` but have different `variantTypeName`s (e.g. two ad-hoc variants PoleOS tagged
 * with the same code but different display names) — each gets its own checkbox, so filtering
 * must match on the `(variantType, variantTypeName)` pair, not `variantType` alone.
 * `variantType` is the empty string (`EXISTING_VARIANT_GROUP`) for the synthetic "Existing"
 * checkbox, which selects the parent (non-variant) collections themselves.
 *
 * `label` is the checkbox's display text (e.g. "Existing", "Proposed") and becomes the downloaded
 * file's name, so the server never needs to re-derive or translate it.
 */
export interface VariantExportSelection {
	variantType: VariantExportGroup;
	variantTypeName?: string;
	label: string;
}

/**
 * Stable string key for a `(variantType, variantTypeName)` pair — used to dedupe/identify
 * checkboxes client-side and to keep per-group cache keys unique server-side. Empty-string and
 * undefined `variantTypeName` are treated the same (no display name supplied).
 */
export function variantSelectionKey(variantType: string, variantTypeName?: string): string {
	return JSON.stringify([variantType, variantTypeName || '']);
}

export function isExportType(type: string): type is ExportType {
	return isFastExportType(type) || isLongRunningExportType(type);
}

export function isFastExportType(type: string): type is FastExportType {
	return FAST_EXPORT_TYPES.has(type);
}

export function isLongRunningExportType(type: string): type is LongRunningExportType {
	return LONG_RUNNING_EXPORT_TYPES.has(type);
}

const VARIANT_EXPORT_TYPES: ReadonlySet<string> = new Set<LongRunningExportType>([
	'KML_VARIANTS',
	'JSON_VARIANTS',
	'EXCEL_VARIANTS',
]);

/** True when an export asks for variant-aware output: a `*_VARIANTS` type, or any selected variant group. */
export function isVariantExportRequest(
	exportType: string,
	variantGroups: readonly VariantExportSelection[] | undefined
): boolean {
	return VARIANT_EXPORT_TYPES.has(exportType) || (variantGroups != null && variantGroups.length > 0);
}

type OptionalValidationMessageFeature = {
	customizedFieldTitle?: string;
};

type ValidationMessageBase =
	| {
			reason: 'pla_multiple_spans_linked' | 'pla_collection_link_one_way';
			poleCollectionTitle: string;
			targetCollectionTitle: string;
	  }
	| {
			reason: 'pla_count_on_span_mismatch';
			selfCount: number;
			otherCount: number;
			type: string;
	  }
	| {
			reason: 'pla_span_same_type_linked';
			linkedPole: string;
			type: string;
	  }
	| {
			reason: 'ipf_span_other_to_other_linked';
			linkedPole: string;
	  }
	| {
			reason: 'pla_power_circuit_phase_height_mismatch';
			phase: string;
			linkedPole: string;
			missingSideTitle: string;
			populatedSideTitle: string;
	  }
	| {
			reason: 'pla_power_circuit_group_mismatch';
			group: string;
			linkedPole: string;
			missingSideTitle: string;
			populatedSideTitle: string;
	  }
	| {
			reason: 'pla_circuit_type_mismatch';
			linkedPole: string;
			circuitCategory: string;
			thisType: string;
			otherType: string;
	  }
	| {
			reason: 'shapefile_value_truncated';
			columnSize: number;
	  }
	| {
			reason: 'shapefile_line_style_conflict';
	  }
	| {
			reason: 'shapefile_asset_type_style_conflict';
	  }
	| {
			reason: 'variant_form_not_found';
			variantLabel: string;
	  }
	| {
			reason: // Legacy codes
				| 'clientitem'
				| 'agl'
				| 'nolocation'
				| 'noguywire'
				| 'missing'
				| 'other'
				| 'reason_only'
				// IKE PoleForeman codes
				| 'ipf_equipment_orientation_missing'
				| 'ipf_equipment_attachment_height_missing'
				| 'ipf_anchor_lead_length_missing'
				| 'ipf_guy_attachment_height_missing'
				| 'ipf_service_cannot_be_collection_linked'
				| 'ipf_service_length_missing'
				| 'ipf_service_endpoint_missing'
				| 'ipf_cycle_detected'
				| 'ipf_isolated_poles_detected'
				| 'ipf_required_field'
				| 'ipf_power_circuit_needs_group'
				| 'ipf_power_circuit_primary_phase_height_missing'
				| 'ipf_power_circuit_secondary_phase_height_missing'
				| 'ipf_target_collection_not_found'
				| 'ipf_anchor_too_many_guys'
				| 'ipf_guy_insulator_type_not_found'
				| 'ipf_location_fallback_to_photo'
				| 'ipf_secondary_multiplex_more_than_1_attachment_height'
				| 'ipf_altitude_zero'
				| 'ipf_pole_soil_class_missing';
	  }
	| {
			reason: 'ipf_equipment_type_not_found';
			equipmentType: string;
			displayDescription: string;
	  }
	| {
			reason:
				| 'ipf_anchor_assembly_type_not_found'
				| 'ipf_guy_type_not_found'
				| 'ipf_power_circuit_type_not_found'
				| 'ipf_framing_type_not_found'
				| 'ipf_service_multiple_power_circuits'
				| 'ipf_service_multiple_telephone_cables'
				| 'ipf_service_multiple_catv_cables';
			displayDescription: string;
	  }
	| {
			reason: 'ipf_multiple_spans_same_type';
			poleCollectionTitle: string;
	  }
	| {
			reason: 'ipf_conductor_type_not_found' | 'ipf_joint_use_cable_type_not_found';
			displayDescription: string;
			rulingSpan: number;
	  }
	| {
			reason: 'spida_calc_title_invalid';
			displayDescription: string;
	  }

	// spidacalc span linking validation messages
	| {
			reason:
				| 'spida_calc_span_linking_target_pole_not_found'
				| 'spida_calc_span_linking_span_mismatch'
				| 'spida_calc_span_linking_wires_count_mismatch'
				| 'spida_calc_span_linking_wires_too_many'
				| 'spida_calc_span_linking_span_guys_count_mismatch'
				| 'spida_calc_span_linking_wire_usage_group_mismatch'
				| 'spida_calc_span_linking_wire_size_mismatch'
				| 'spida_calc_span_linking_wire_tension_group_mismatch'
				| 'spida_calc_span_linking_wire_owner_mismatch'
				| 'spida_calc_span_linking_span_guy_owner_mismatch'
				| 'spida_calc_span_linking_span_guy_usage_group_mismatch'
				| 'spida_calc_span_linking_span_guy_size_mismatch'
				| 'spida_calc_span_linking_span_guy_tension_group_mismatch'
				| 'spida_calc_span_linking_span_guy_height_exceed_agl'
				| 'spida_calc_span_linking_span_guy_attachment_height_exceed_agl'
				| 'spida_calc_span_linking_span_guy_mid_span_height_exceed_agl';
	  }

	// dds span linking validation messages
	| {
			reason:
				| 'dds_required_field'
				| 'dds_recommended_field'
				| 'dds_location_fallback_to_photo'
				| 'dds_negative_circumference'
				| 'dds_temperature_without_midspan'
				| 'dds_temperature_negative';
	  }
	| {
			reason: 'dds_equipment_type_not_found';
			equipmentType: string;
			displayDescription: string;
	  };

export type ValidationMessage = ValidationMessageBase & OptionalValidationMessageFeature;

export interface ExportNotificationEntry {
	code: 'required' | 'default' | 'invalid' | 'success' | 'other' | 'reason_only';
	field?: {
		/** Subform instance id; `null` means the field lives on the top-level form. */
		formId: string | null;
		fieldId: string;
		/** If we cannot determine the formId the value of the field is returned instead */
		value?: string;
	};
	/**
	 * Extra information about why the field has a notification
	 * for example missing clientitem - 'clientitem'
	 * or AGL is above height 'agl'
	 */
	reason?: ValidationMessage;
	defaultValue?: string;
}

export interface ExportNotification {
	/**
	 * Discriminator for the umbrella `Notification` union on long-running task documents.
	 * Optional for backwards compatibility — absent `kind` is treated as 'export' at read-time.
	 */
	kind?: 'export';
	revision: string;
	warnings: ExportNotificationEntry[];
	errors: ExportNotificationEntry[];
}

// --- Fast export action ---

export interface IkeActionFastExportRequest {
	type: FastExportType;
	revisions: string[];
	timezone: string;
	noPhotos?: boolean;
}

export interface IkeActionFastExportResponse {
	notifications: ExportNotification[];
	fileName: string | null;
}

export type IkeActionFastExportUrl = 'export.fast';

export interface IkeActionFastExport extends IkeAction<
	IkeActionFastExportRequest,
	IkeActionFastExportResponse,
	IkeActionFastExportUrl
> {}

// --- Legacy export action (deprecated) ---

/** @deprecated Use the new export system instead. */
export type LEGACYSelectorsByTag = { [tagId: string]: CollectionSelector[] };

/** @deprecated Use IkeActionFastExportRequest or task route instead. */
export interface LEGACYIkeActionExportRequest {
	type: ExportType;
	tags: LEGACYSelectorsByTag;
	skipCache?: boolean;
	time: string;
	units: IkeUnitDistance;
	timezone?: string;
	noPhotos?: boolean;
}

/** @deprecated Use IkeActionFastExportResponse or task status route instead. */
export interface LEGACYIkeActionExportResponse {
	notifications: ExportNotification[];
	exportId?: string;
	url?: string;
	fileName?: string;
}

/** @deprecated */
export type LEGACYIkeActionExportUrl = 'export';

/** @deprecated */
export interface LEGACYIkeActionExport extends IkeAction<
	LEGACYIkeActionExportRequest,
	LEGACYIkeActionExportResponse,
	LEGACYIkeActionExportUrl
> {}
